/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { CSSProperties } from "react";

import debounce from "lodash/debounce";
import { Descendant, Node } from "slate";
import { DefaultTheme } from "styled-components";
import { v4 as uuid } from "uuid";

import { BlockType, ONE_SECOND, TextType } from "@vienna/common";
import {
  BaseBlockSnapshot,
  CodeBlockSnapshot,
  ColorBlockSnapshot,
  ImageBlockSnapshot,
  LinkBlockSnapshot,
  TextBlockSnapshot,
} from "@vienna/common/src/entities/block-snapshot.entity";

import { ClipboardUtils } from "../../utilities/ClipboardUtils";
import { BaseBlock } from "src/classes/block/BaseBlock";
import { PlateSocket } from "src/classes/plate/PlateSocket/PlateSocket";

import { CodeBlock } from "./CodeBlock";
import { ColorBlock } from "./ColorBlock";
import { ImageBlock } from "./ImageBlock";
import { LinkBlock } from "./link-block";

export type ConvertibleBlockType = BlockType.Code | BlockType.Color | BlockType.Image | BlockType.Link; // types you can change to via the slash menu
export class TextBlock extends BaseBlock<TextBlockSnapshot> {
  constructor(snapshot: TextBlockSnapshot, plateSocket: PlateSocket | null) {
    super(snapshot, plateSocket);
  }

  /* -------------------------------------------------- */
  /*                       State                        */
  /* -------------------------------------------------- */
  public changeTextType(textType: TextType): void {
    if (!this.plateSocket) return;
    this.setState({ textType });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  /**
   * This function is public because it is needed to quickly remove the "/" from the block when the user selects a new block type from the slash menu.
   * In every other case, you should use the debouncedUpdateTextContent function.
   */
  public immediatelyUpdateTextContent = (textContent: Descendant[]): void => {
    if (!this.plateSocket) return;
    const plaintext = textContent.map((n) => Node.string(n)).join("\n");
    this.setState({ textContent, plainText: plaintext });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  };

  public debouncedUpdateTextContent = debounce(this.immediatelyUpdateTextContent, ONE_SECOND / 2, {
    maxWait: ONE_SECOND * 2,
  });

  /* -------------------------------------------------- */
  /*                       Actions                      */
  /* -------------------------------------------------- */

  public copyText(): void {
    ClipboardUtils.copyTextToClipboard(this.snapshot.plainText);
  }

  public get searchLink(): string {
    return `http://www.google.com/search?q=${this.snapshot.plainText}`;
  }

  /* -------------------------------------------------- */
  /*                  Editor Styling                    */
  /* -------------------------------------------------- */
  public get textEditorId(): string {
    return `slate-${this.snapshot.id}`;
  }

  public getEditorStyles(theme: DefaultTheme): CSSProperties {
    return {
      height: "100%",
      width: "100%",
      color: theme.text.default,
      fontSize: `${this.fontSize}px`,
      fontWeight: [TextType.H1, TextType.H2].includes(this.snapshot.textType) ? "500" : "normal",
      fontFamily: this.snapshot.textType === TextType.Mono ? "IBM Plex Mono" : "Inter",
    };
  }

  private get fontSize(): number {
    switch (this.snapshot.textType) {
      case TextType.H1:
        return 30;
      case TextType.H2:
        return 24;
      default:
        return 14;
    }
  }

  /* -------------------------------------------------- */
  /*                      Create                        */
  /* -------------------------------------------------- */
  public static newSnapshot(baseSnapshot: BaseBlockSnapshot): TextBlockSnapshot {
    const snapshot: TextBlockSnapshot = {
      ...baseSnapshot,
      type: BlockType.Text,
      textType: TextType.Plain,
      plainText: "",
      textContent: [],
    };
    return snapshot;
  }

  /* -------------------------------------------------- */
  /*                Snapshot Conversion                 */
  /* -------------------------------------------------- */
  public static convertSnapshot(
    snapshot: TextBlockSnapshot,
    newType: ConvertibleBlockType
  ): ImageBlockSnapshot | CodeBlockSnapshot | ColorBlockSnapshot | LinkBlockSnapshot {
    const baseSnapshot = { ...this.pickOffBaseSnapshot(snapshot), id: uuid() };
    switch (newType) {
      case BlockType.Image:
        return ImageBlock.newSnapshot(baseSnapshot);
      case BlockType.Code:
        return CodeBlock.newSnapshot(baseSnapshot, snapshot.plainText);
      case BlockType.Color:
        return ColorBlock.newSnapshot(baseSnapshot);
      case BlockType.Link:
        return LinkBlock.newSnapshot(baseSnapshot, snapshot.plainText);
    }
  }

  private static pickOffBaseSnapshot(snapshot: TextBlockSnapshot): BaseBlockSnapshot {
    const {
      id,
      top,
      left,
      width,
      height,
      zIndex,
      userId,
      isLiked,
      plateId,
      createdAt,
      connectionCount,
      currentUserIsOwner,
    } = snapshot;
    return {
      id,
      top,
      left,
      width,
      height,
      userId,
      zIndex,
      isLiked,
      plateId,
      createdAt,
      connectionCount,
      currentUserIsOwner,
    };
  }
}
